import React, {
  useState, useEffect, useCallback,
} from 'react';
import {
  Button, Col, Divider, Row, Input,
  Spin, Table, Form, Modal, notification,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import FilterIcon from 'mdi-react/FilterIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import {
  DeleteOutlined,
  EditOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import moment from 'moment/moment';
import { EquipmentModel } from '../../../sharedTypes/EquipmentModelsTypes';
import './EquipamentModel.scss';
import {
  patch, post, put, del,
  ResponseWrapper, RequestWrapper, isSuperAgentError,
} from '../../../dataSource/requests';
import CustomPagination from '../../sharedComponents/customPagination/CustomPagination';
import { backendResponseCodes } from '../../../utils/responseMessages';

const EquipamentModel = (): JSX.Element => {
  interface RelevantPaginationInfo {
    maxSize: number;
    numberOfPages: number;
    pageNumber: number;
    totalElements: number;
  }

  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const [formAddInstance] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [edittingModel, setEdditingModel] = useState(undefined);
  const [data, setData] = useState<Array<EquipmentModel>>([]);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [paginationInfo, setPaginationInfo] = useState<RelevantPaginationInfo>({
    maxSize: 30,
    numberOfPages: 1,
    pageNumber: 1,
    totalElements: 1,
  });

  const showModal = (edit): void => {
    formAddInstance.setFieldsValue({
      nameInput: edit != null ? edit.name : '',
    });
    setEdditingModel(edit);
    setVisibleModal(true);
  };

  const getFilterParams = useCallback(() => {
    const values = formInstance.getFieldsValue();
    const params = {};

    if (values.nameFilter != undefined) {
      params.name = values.nameFilter;
      return params;
    }

    return undefined;
  }, [formInstance]);

  const fetchData = useCallback(async (page) => {
    let manageBody = getFilterParams();

    manageBody == null ? manageBody = {} : manageBody = manageBody;
    manageBody.name == null ? manageBody.name = '' : manageBody.name = manageBody.name;

    setLoading(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = patch({
      url: '/equipament-model/listAllWithFilter',

      query: {
        name: manageBody.name,
        pageNumber: page - 1,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 14001):
        // "Equipament model listed successfully", 14001
        setData(
          response?.body?.equipamentModels != undefined
            ? response?.body?.equipamentModels?.sort((a, b) => a.name > b.name)
            : [response?.body?.equipamentModel],
        );
        setPaginationInfo((previous) => {
          return ({
            ...previous,
            ...response?.body?.paginationInfo,
          });
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar a listagem, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  }, [getFilterParams]);

  const columns = [
    {
      title: <div className="table-titles">ID</div>,
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: <div className="table-titles">Nome</div>,
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: <div className="table-titles">Data/hora de Criação</div>,
      dataIndex: 'creationDate',
      key: 'creationDate',
      render: (date: string) => (date ? moment(date).format('DD/MM/YYYY [às] HH:mm') : ''),
    },
    {
      title: <div className="table-titles">Data/hora de edição</div>,
      dataIndex: 'editionDate',
      key: 'editionDate',
      render: (date: string) => (date ? moment(date).format('DD/MM/YYYY [às] HH:mm') : ''),
    },
    {
      title: <div className="table-titles">Ações</div>,
      dataIndex: 'id',
      key: 'opcoes',

      render: (id: number) => (
        <span>
          <Button
            icon={<EditOutlined />}
            onClick={
              (): void => {
                showModal(
                  data.find(
                    (field) => {
                      return field.id === id;
                    },
                  ),
                );
              }
            }
          />
          <Divider type="vertical" />
          <Button
            icon={<DeleteOutlined />}
            onClick={
              (): void => {
                Modal.confirm({
                  title: 'Deseja realmente excluir?',
                  content: 'A exclusão desse modelo de equipamento não será refletida nos serviços ou orçamentos vinculados a ele.',
                  okText: 'Sim, excluir',
                  cancelText: 'Não, cancelar',
                  autoFocusButton: null,
                  icon: (<WarningOutlined />),
                  onOk: () => {
                    const deleteEquipamentModel = async (_id): Promise<void> => {
                      let response: ResponseWrapper | undefined;
                      let responseError: ResponseWrapper | undefined;
                      let genericError: Error | undefined;

                      setLoading(true);

                      try {
                        const requestReference: RequestWrapper = del({
                          url: `/equipament-model/deleteEquipamentModel/${_id}`,

                          frontConfigs: {
                            navigateFunction: navigate,
                          },
                        });

                        response = await requestReference;
                      } catch (error) {
                        if (isSuperAgentError(error)) {
                          responseError = error.response;
                        } else {
                          genericError = error as Error;
                        }
                      }

                      switch (true) {
                        case (response != null && response.body.code === 14002):
                          // "Equipament model deleted successfully", 14002
                          notification.success({
                            message: 'Sucesso',
                            description: 'O modelo foi removido com sucesso!',
                          });

                          fetchData(1);
                          break;

                        case (responseError != null && responseError.body.code === -4001):
                          // "You must be authenticated to perform this operation", -4001
                          break;

                        case (responseError != null && responseError.body.code === -15002):
                          // "Equipament model not found", -15002
                          notification.error({
                            message: 'Erro',
                            description: backendResponseCodes.Error.EquipamentModel
                              .EquipamentModelNotFound.description
                              .pt_br,
                          });
                          break;

                        case (responseError != null && responseError.body.code === -15003):
                          // Equipment linked to a service", -15003
                          notification.error({
                            message: 'Erro',
                            description: 'Desculpe, mas este modelo está vinculado a um serviço e não pôde ser removido.',
                          });
                          break;

                        case (genericError?.message?.includes('has been aborted')):
                          // dont show anything when request is aborted
                          break;

                        default:
                          notification.error({
                            message: 'Erro',
                            description: 'Houve um erro ao deletar o modelo, tente novamente',
                          });
                          break;
                      }

                      setLoading(false);
                    };

                    deleteEquipamentModel(id);
                  },
                });
              }
            }
          />
        </span>
      ),
    },
  ];

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const handleSubmit = async (nameValue): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      await formAddInstance.validateFields();
    } catch (error) {
      notification.warning({
        message: 'Atenção',
        description: 'Informações inválidas, verifique e tente novamente por favor',
      });

      return;
    }

    setLoading(true);
    const today = new Date();
    if (edittingModel != undefined) {
      try {
        const requestReference: RequestWrapper = put({
          url: '/equipament-model/editEquipamentModel',

          params: {
            name: nameValue,
            id: edittingModel.id,
          },

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 14003):
          // "Equipament model edited successfully", 14003
          notification.success({
            message: 'Sucesso',
            description: backendResponseCodes.Success.EquipamentModel
              .EquipamentModelEditedSuccessfully
              .description.pt_br,
          });

          setLoading(false);
          setVisibleModal(false);
          fetchData(1);
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          setLoading(false);
          setVisibleModal(false);
          break;

        case (responseError?.body?.code === -15002):
          // "Equipament model not found", -15002
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.EquipamentModel
              .EquipamentModelNotFound
              .description.pt_br,
          });
          setLoading(false);
          setVisibleModal(false);
          break;

        case (responseError?.body?.code === -15001):
          // "Equipament model name can't be null", -15001
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.EquipamentModel
              .EquipamentModelNameCantBeNull
              .description.pt_br,
          });
          setLoading(false);
          setVisibleModal(false);
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao editar o modelo, por favor '
              + 'tente novamente mais tarde.',
          });
          setLoading(false);
          setVisibleModal(false);
          break;
      }
    } else {
      try {
        const requestReference: RequestWrapper = post({
          url: '/equipament-model/createEquipamentModel',

          params: {
            name: nameValue,
            creationDate: today,
            editionDate: today,
          },

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      setLoading(false);
      setVisibleModal(false);

      switch (true) {
        case (response?.body?.code === 14000):
          // "Equipament model registered successfully", 14000
          notification.success({
            message: 'Sucesso',
            description: backendResponseCodes.Success.EquipamentModel
              .EquipamentModelRegisteredSuccessfully
              .description.pt_br,
          });

          fetchData(1);
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -15000):
          // "Equipament model name already in use", -15000
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.EquipamentModel
              .EquipamentModelNameAlreadyInUse
              .description.pt_br,
          });
          break;

        case (responseError?.body?.code === -15001):
          // "Equipament model name can't be null", -15001
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.EquipamentModel
              .EquipamentModelNameCantBeNull
              .description.pt_br,
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao adicionar o modelo, por favor '
              + 'tente novamente mais tarde.',
          });
          break;
      }
    }
  };

  const handleFilter = (): void => {
    fetchData(1);
  };

  const cleanFilterFields = (): void => {
    formInstance.setFieldsValue({
      nameFilter: '',
    });
    fetchData(1);
  };

  return (
    <div className="EquipamentModel">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          onFinish={handleFilter}
        >
          <Row gutter={24} className="title-container">
            <Col xs={{ span: 24 }} md={{ span: 24 }}>
              <span className="page-title">Modelos de equipamento</span>
            </Col>
          </Row>

          <Row gutter={24} className="filters-container">
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
                name="nameFilter"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Nome"
                >
                </Input>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-clean"
                  htmlType="reset"
                  onClick={
                    (): void => {
                      cleanFilterFields();
                    }
                  }
                >
                  <FilterRemoveIcon />
                  Limpar
                </Button>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-filter"
                  type="primary"
                  htmlType="submit"
                >
                  <FilterIcon />
                  Filtrar
                </Button>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-filter"
                  type="primary"
                  onClick={
                    (): void => {
                      showModal(null);
                    }
                  }
                >
                  <PlusIcon />
                  Adicionar
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    rowKey="actionDate"
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <CustomPagination
                    paginationInfo={paginationInfo}
                    fetchFunc={fetchData}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
      <Modal
        className="add-modal"
        title={<h3 style={{ margin: 0 }}>Cadastrar modelo de equipamento</h3>}
        onCancel={
          (): void => {
            setVisibleModal(false);
          }
        }
        visible={visibleModal}
        footer={[
          <Button
            className="btn-ok"
            type="primary"
            key="submit"
            onClick={
              async (): Promise<void> => {
                if (edittingModel != undefined) {
                  Modal.confirm({
                    title: 'Deseja realmente alterar?',
                    content: 'A alteração desse modelo de equipamento não será refletida nos serviços ou orçamentos vinculados a ele.',
                    okText: 'Sim, alterar',
                    cancelText: 'Não, cancelar',
                    autoFocusButton: null,
                    icon: (<WarningOutlined />),
                    onOk: async () => {
                      try {
                        await formAddInstance.validateFields();
                      } catch (error) {
                        console.log('erro', error);
                        return;
                      }

                      const v = formAddInstance.getFieldValue('nameInput');
                      handleSubmit(v);
                    },
                  });
                } else {
                  try {
                    await formAddInstance.validateFields();
                  } catch (error) {
                    console.log('erro', error);
                    return;
                  }

                  const v = formAddInstance.getFieldValue('nameInput');
                  handleSubmit(v);
                }
              }
            }
          >
            Salvar
          </Button>,
        ]}
      >
        <Col>
          <Form
            form={formAddInstance}
          >
            <Form.Item
              label=""
              name="nameInput"
              rules={[
                { required: true, message: 'Por favor, insira um nome' },
                { max: 50, message: 'Máximo de 50 caracteres' },
              ]}
            >
              <Input
                style={{ width: '100%' }}
                placeholder="Nome"
              >
              </Input>
            </Form.Item>
          </Form>
        </Col>
      </Modal>
    </div>
  );
};

EquipamentModel.propTypes = {};

EquipamentModel.defaultProps = {};

export default EquipamentModel;
